import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Card, CardContent, Alert, Divider, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import Spinner from '../../components/Spinner';

const ReadingLounge: React.FC = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState<any[]>([]);
  const [selectedPassage, setSelectedPassage] = useState<any | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Quiz state
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [graded, setGraded] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const fetchPassages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let queryStr = `?type=reading`;
      if (search.trim()) {
        queryStr += `&search=${encodeURIComponent(search.trim())}`;
      }

      const res = await api.get(`/language-hub${queryStr}`);
      if (res.data && res.data.success) {
        setPassages(res.data.data);
      } else {
        setError('Failed to fetch reading passages.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to reading lounge API');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchPassages();
  }, [fetchPassages]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPassages();
  };

  const selectPassage = (passage: any) => {
    setSelectedPassage(passage);
    setAnswers({});
    setGraded(false);
    setScore(null);
  };

  const handleAnswerChange = (qIndex: number, option: string) => {
    if (graded) return;
    setAnswers(prev => ({ ...prev, [qIndex]: option }));
  };

  const handleGradeQuiz = () => {
    if (!selectedPassage || !selectedPassage.quizzes) return;
    
    let correctCount = 0;
    selectedPassage.quizzes.forEach((quiz: any, idx: number) => {
      if (answers[idx] === quiz.correctAnswer) {
        correctCount++;
      }
    });

    setScore(correctCount);
    setGraded(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back button */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => {
          if (selectedPassage) {
            setSelectedPassage(null);
          } else {
            navigate('/language-hub');
          }
        }} 
        sx={{ mb: 4 }}
      >
        {selectedPassage ? 'All Passages' : 'Language Hub'}
      </Button>

      {/* Main passages selection list */}
      {!selectedPassage && (
        <Box>
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
              Reading Lounge
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enhance reading comprehension. Select a passage to read and take a graded quiz to test your understanding.
            </Typography>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 5 }}>
            <form onSubmit={handleSearchSubmit}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search passages by title or topic..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
                <Button type="submit" variant="contained" size="large" sx={{ px: 4 }}>
                  Search
                </Button>
              </Box>
            </form>
          </Box>

          {/* Error display */}
          {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

          {/* Passages grid list */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Spinner />
            </Box>
          ) : passages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 4 }}>
              <Typography variant="h6" color="text.secondary">No comprehension passages found.</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {passages.map((item) => (
                <Grid item xs={12} sm={6} key={item._id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderRadius: 4, 
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      background: 'rgba(255, 255, 255, 0.01)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#8b5cf6',
                        background: 'rgba(255, 255, 255, 0.02)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          {item.title}
                        </Typography>
                        <Button variant="outlined" color="secondary" size="small">
                          {item.category || 'General'}
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', lineHeight: 1.6 }}>
                        {item.passage}
                      </Typography>
                      <Button variant="contained" color="secondary" fullWidth onClick={() => selectPassage(item)}>
                        Read & Start Quiz
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Individual Passage reading & quiz view */}
      {selectedPassage && (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{selectedPassage.title}</Typography>
            <Typography variant="subtitle2" sx={{ color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: 1 }}>
              {selectedPassage.category} level
            </Typography>
          </Box>

          {/* Passage Text */}
          <Card sx={{ p: 4, borderRadius: 4, mb: 5, background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {selectedPassage.passage}
            </Typography>
          </Card>

          <Divider sx={{ my: 5 }} />

          {/* Comprehension Quiz section */}
          {selectedPassage.quizzes && selectedPassage.quizzes.length > 0 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleOutlineIcon color="secondary" />
                Comprehension Check
              </Typography>

              {graded && score !== null && (
                <Alert 
                  severity={score === selectedPassage.quizzes.length ? 'success' : 'info'} 
                  sx={{ mb: 4, borderRadius: 3 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Your Score: {score} / {selectedPassage.quizzes.length}
                  </Typography>
                  <Typography variant="body2">
                    {score === selectedPassage.quizzes.length ? 'Perfect score! Excellent reading comprehension!' : 'Review the explanations below to improve your skills.'}
                  </Typography>
                </Alert>
              )}

              {selectedPassage.quizzes.map((quiz: any, qIdx: number) => {
                const selected = answers[qIdx] || '';
                const isCorrect = selected === quiz.correctAnswer;
                
                return (
                  <Card key={qIdx} sx={{ mb: 4, p: 3, borderRadius: 3, border: '1px solid rgba(255,255,255,0.05)', background: 'transparent' }}>
                    <CardContent sx={{ p: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {qIdx + 1}. {quiz.question}
                      </Typography>

                      <RadioGroup
                        value={selected}
                        onChange={(e) => handleAnswerChange(qIdx, e.target.value)}
                      >
                        <Grid container spacing={2}>
                          {quiz.options.map((option: string, oIdx: number) => {
                            let optionColor = 'inherit';
                            if (graded) {
                              if (option === quiz.correctAnswer) optionColor = 'success.main';
                              else if (selected === option) optionColor = 'error.main';
                            }

                            return (
                              <Grid item xs={12} key={oIdx}>
                                <Box 
                                  sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    border: '1px solid rgba(255,255,255,0.04)',
                                    backgroundColor: selected === option ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                                    color: optionColor
                                  }}
                                >
                                  <FormControlLabel
                                    value={option}
                                    control={<Radio color="secondary" disabled={graded} />}
                                    label={option}
                                    sx={{ width: '100%', m: 0 }}
                                  />
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </RadioGroup>

                      {graded && (
                        <Box sx={{ mt: 3, p: 2, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.02)', borderLeft: `3px solid ${isCorrect ? '#10b981' : '#ef4444'}` }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: isCorrect ? 'success.light' : 'error.light', mb: 0.5 }}>
                            {isCorrect ? 'Correct!' : `Incorrect (Correct answer: ${quiz.correctAnswer})`}
                          </Typography>
                          {quiz.explanation && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Explanation:</strong> {quiz.explanation}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {!graded ? (
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large" 
                  fullWidth 
                  onClick={handleGradeQuiz}
                  disabled={Object.keys(answers).length < selectedPassage.quizzes.length}
                >
                  Grade Quiz
                </Button>
              ) : (
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  size="large" 
                  fullWidth 
                  onClick={() => selectPassage(selectedPassage)}
                >
                  Retake Quiz
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default ReadingLounge;
